import { action, computed, makeObservable, observable } from 'mobx';
import { DateTime, Duration } from 'luxon';
import {
  AddMomentInput,
  DeleteMomentInput,
  Job,
  JobSource,
  JobStatus,
  UpdateMomentInput,
  IngestError,
  PageInfo,
  AddCommentInput,
  AddReplyInput,
  EditCommentInput,
  ResizedImage,
  AddReactionInput,
  JobFeatures,
  JobFeatureStatus,
  UpdateJobInput,
  Industry,
  Metadata,
  MedicalSpecialty,
  GetMomentQuery,
  JobType,
  JobSourceType,
  LiveStreamActivePlaylist,
  LiveStream,
  LiveStreamPlaylist,
  LiveStreamRecordings,
  LiveStreamChannels,
  Highlight,
  GenerativeAiProvider,
  JobSpeciality,
  isDualPlusPublicSafetySpeciality,
  RedactionRequest,
} from '@clipr/lib';
import { PartialDeep } from 'type-fest';
import { Owner } from '../entities/owner';
import { Answer, Moment, MomentModel, MomentProps, Question, SubTopic, Topic } from './moment';
import { TrackModel, TrackProps } from './track';
import { MediaInfo } from './media';
import { StoreNode } from '../store';
import { Store } from '../store/store';
import { assert, AsyncResult, Maybe, Nullable } from '../core';
import { Bookmark } from './bookmark';
import { PermissionType } from './permission';
import { ApiVariables, ApiMutationOptions, ApiQueryOptions } from '../api/apiSchema';
import { TeamProps } from './team';
import { MomentQueryable } from './moments';
import { SpeakerModel } from './speaker';
import { diffReadable, durationToString, timestampNorm } from '../core/time';
import { Comment, CommentProps, Reaction, ReactionProps } from './comment';
import { EnrichmentItem, EnrichmentItems } from './job/jobFeatures';
import { input, InputState } from '../components';
import { notifyError, notifySuccess } from '../services/notifications';
import { AnalyticsEventTypes } from '../services/analytics/analyticsSchema';
import { JobLiveAggregateStatus, MediaAggregatedStatus } from '.';
import { toValidDateTime } from '../core/dateTime';
import {
  sortedMoments,
  visibleMomentsFrom,
  filterTranscriptMoments,
  filterTopicMoments,
  filterSubtopicMoments,
  filterRedactionMoments,
  filterGenericMoments,
  filterAnswerMoments,
  filterQuestionMoments,
  filterActiveTopics,
  filterActiveSubtopics,
  filterActiveAnswers,
  filterActiveQuestions,
  hasMomentInLookup,
  getMomentFromLookup,
  maybeGetMomentFromLookup,
  insertMomentIntoLookup,
  buildFetchMomentsArgs,
  apiFetchMoments as apiFetchMomentsHelper,
  apiFetchMoment as apiFetchMomentHelper,
  apiAddMoment as apiAddMomentHelper,
  apiUpdateMoment as apiUpdateMomentHelper,
  apiUpdateMoments as apiUpdateMomentsHelper,
  apiDeleteMoment as apiDeleteMomentHelper,
  apiDeleteMoments as apiDeleteMomentsHelper,
} from './jobMoments';
import {
  sortedTracks,
  filterActiveTracks,
  filterTranscriptTracks,
  filterMomentTracks,
  filterTopicTracks,
  filterSubtopicTracks,
  filterActiveTopicTracks,
  findActiveTopicTrack,
  filterActiveSubtopicTracks,
  findActiveSubtopicTrack,
  hasTrackInLookup,
  getTrackFromLookup,
  findTrackByName,
  findTranscriptTrackBySpeaker,
  findMomentTrackByMomentType,
  insertTrackIntoLookup,
  findMultipleActiveTrackTypes,
  validateSetToDone as validateSetToDoneHelper,
  findActiveDuplicateByTrack,
  checkIfActiveDuplicateForTrack as checkIfActiveDuplicateForTrackHelper,
  collectSpeakerIds,
  filterSpeakersByIds,
  filterPrivateSpeakersByIds,
  collectTrackSpeakerIds,
  resolveTrackSpeakers,
  apiFetchTracks as apiFetchTracksHelper,
  apiAddTrack as apiAddTrackHelper,
  apiUpdateTrack as apiUpdateTrackHelper,
  apiMergeTracks as apiMergeTracksHelper,
  apiDuplicateTrack as apiDuplicateTrackHelper,
  apiFetchSpeakers as apiFetchSpeakersHelper,
  apiBatchFetchSpeakers as apiBatchFetchSpeakersHelper,
  addSpeaker as addSpeakerHelper,
  apiUpdateSpeaker as apiUpdateSpeakerHelper,
} from './jobTracks';
import {
  sortedComments,
  sortedReactions,
  getCommentFromLookup,
  insertCommentIntoLookup,
  deleteCommentFromLookup,
  getReactionFromLookup,
  insertReactionIntoLookup,
  deleteReactionFromLookup,
  apiFetchComments as apiFetchCommentsHelper,
  fetchComments as fetchCommentsHelper,
  apiFetchReactions as apiFetchReactionsHelper,
  fetchReactions as fetchReactionsHelper,
  apiAddComment as apiAddCommentHelper,
  apiAddReply as apiAddReplyHelper,
  apiEditComment as apiEditCommentHelper,
  apiDeleteComment as apiDeleteCommentHelper,
  apiAddReaction as apiAddReactionHelper,
} from './jobComments';
import {
  checkPermission,
  isOwnerAuthenticated as isOwnerAuthenticatedHelper,
  relatedBookmarks as relatedBookmarksHelper,
  relatedMomentBookmarks as relatedMomentBookmarksHelper,
  relatedTopicMomentBookmarks as relatedTopicMomentBookmarksHelper,
  relatedGenericMomentBookmarks as relatedGenericMomentBookmarksHelper,
} from './jobPermissions';


export interface ReportQuoteIssue {
  readonly quote: string;
  readonly closest?: string | null;
}

const OWNER_TEAM_PREFIX = `TEAM#`;
const OWNER_USER_PREFIX = `USER#`;

export type ValidatorResponse = {
  multipleActiveTrackTypes: string[];
  moments: MomentModel[],
  error: boolean
}

/**
 * Returns the first Topic moment after the specified target moment.
 * IMPORTANT! This method assumes that the input list of moments is already sorted
 * (as it should be on the Job entity, for example).
 */
export function nextTopicAfter(
  moments: MomentModel[],
  target: MomentModel): Nullable<MomentModel> {
  for (let i = 0; i < moments.length; i++) {
    const mom = moments[i];
    if (mom.startTime > target.startTime && mom.clipType === 'Topic')
      return mom;
  }
  return null;
}

export function isJob(arg: unknown): arg is JobModel {
  return (
    arg instanceof JobModel &&
    arg.nodeType === 'Job');
}

export function assertIsJob(arg: unknown): asserts arg is JobModel {
  assert(isJob(arg),
    `Expected ${arg} to be instance of JobModel.`);
}

export enum SyncStatus {
  Fetching = 'Fetching',
  Fetched = 'Fetched',
  Error = 'Error',
  Empty = 'Empty'
}

export type JobProps = PartialDeep<Job> & {
  isTransient?: boolean
  highlights?: Highlight[]
};

export class JobModel
  extends StoreNode {

  constructor(props: Partial<JobProps>, store: Store) {
    super(store);
    makeObservable(this);

    Object.assign(this, props);

    if (props.media)
      this.media = new MediaInfo(props.media);

    this.publicToggleModel = input(this, {
      name: 'toggle-public-video',
      onChange: (_evt) => this.handlePublishVideo(),
      disabled: () => this.isPublishing
    });
    this.publicToggleModel.value = `${!!this.isPublic}`;
    this.foundInTeams = this.visibleToTeams;

    this.isTransient = props.isTransient ?? false;
    this.highlights = props.highlights ?? null;
  }

  readonly nodeType: 'Job' = 'Job';

  declare readonly id: string;
  readonly isTransient: boolean;
  readonly highlights: Highlight[] | null;
  readonly createdAt!: string;
  readonly languageCode!: string;
  readonly lastView!: string;
  readonly media!: MediaInfo;
  readonly momentCount!: number;
  readonly recordingLocation!: string;
  readonly recordingUrl!: string;
  readonly source!: JobSource;
  readonly status!: JobStatus;
  readonly subtitleLocation!: string;
  readonly title!: string;
  readonly description!: string;
  readonly user!: {
    id: string,
    username: string,
    name: string,
    showPlayerTutorial: boolean
  }
  readonly userId!: string;
  readonly durationInMs!: number;
  readonly videoType!: JobType;
  readonly viewCount!: number;
  readonly ingestError!: IngestError;
  readonly ingestExecutionArn!: string;
  readonly progressPercentage!: number;
  readonly ownerId!: string;
  readonly owner!: Owner;
  readonly isPublic!: boolean;
  readonly speciality!: JobSpeciality;
  readonly customSpecialtyId?: string;
  readonly deceptioSpeakerId?: string;
  readonly medicalSpecialty?: MedicalSpecialty;
  readonly isPublished!: boolean;
  readonly sourceLanguageCode?: string;
  readonly targetLanguageCode?: string;
  readonly originalDocumentLocation?: string;

  readonly visibleToTeams!: Partial<TeamProps>[];
  // A mutable copy of visibleToTeams to be used for FE operations like 'Add to Team'
  @observable foundInTeams: Partial<TeamProps>[];

  readonly userPermissions!: JobProps['userPermissions'];
  readonly poster?: ResizedImage[] = [];

  readonly features?: JobFeatures;
  readonly industry?: Industry;
  readonly metadata?: Metadata;
  readonly keywords?: string[];
  readonly tags?: string[];
  readonly sortOrder?: number;
  readonly publicToggleModel: InputState;

  readonly liveStream: LiveStream | null = null;
  readonly adTagUrls!: string[];
  readonly slideDeckUrl: string | null = null;
  readonly availableGptProviders: GenerativeAiProvider[] | null = null;
  readonly publicSafetyNotes: string | null = null;
  readonly publicSafetyCaseId: string | null = null;
  readonly publicSafetyOfficerName: string | null = null;
  readonly publicSafetyOfficerTitle: string | null = null;
  readonly publicSafetyReport: string | null = null;
  readonly publicSafetyManualReport: string | null = null;
  readonly publicSafetyDualPlusReport: string | null = null;
  readonly publicSafetyReportChanged: boolean | null = null;
  readonly publicSafetyReportComplete: boolean | null = null;
  readonly publicSafetyReportRevising: boolean | null = null;
  readonly publicSafetyReportFeedback: boolean | null = null;
  readonly publicSafetyReportCompleteAt: string | null = null;
  readonly relatedPublicSafetyJobs: Partial<Job>[] | null = [];
  /** Quotations the generated report changed from the transcript; null when the check did not run. */
  readonly reportQuoteIssues: ReportQuoteIssue[] | null = null;
  readonly redactionRequest?: RedactionRequest;

  @computed
  get hasDualPlusPublicSafetyJobs(): boolean {
    return isDualPlusPublicSafetySpeciality(this.speciality) && (this.relatedPublicSafetyJobs?.length ?? 0) > 0;
  }

  @computed
  get timeToCertification() {
    if (!this.publicSafetyReportCompleteAt) return null;
    return diffReadable(this.createdAt, this.publicSafetyReportCompleteAt);
  }

  @computed
  get streamedOnLabel() {
    const startingTime = this.liveStreamStartDate?.toLocaleString(DateTime.TIME_SIMPLE);
    const startingDate = this.liveStreamStartDate?.toLocaleString(DateTime.DATE_SHORT);
    if (this.isLiveStreaming)
      return `Started On ${startingDate} at ${startingTime}`;

    if (
      this.liveAggregateStatus &&
      [
        JobLiveAggregateStatus.Processing,
        JobLiveAggregateStatus.ProcessingDone,
        JobLiveAggregateStatus.ProcessingFailed
      ].includes(this.liveAggregateStatus))
      return `Streamed On ${startingDate} at ${startingTime}`;

    return null;
  }

  @computed
  get recordings(): LiveStreamRecordings | null {
    return this.liveStream?.recordings ?? null;
  }

  @computed
  get isPrimarySource(): boolean {
    return this.source?.liveStreamActivePlaylist === LiveStreamActivePlaylist.Primary;
  }

  @computed
  get isExternalSource(): boolean {
    return this.source?.liveStreamActivePlaylist === LiveStreamActivePlaylist.External;
  }

  @computed
  get isBackupSource(): boolean {
    return this.source?.liveStreamActivePlaylist === LiveStreamActivePlaylist.Secondary;
  }

  @computed
  get playlists(): LiveStreamPlaylist | null {
    return this.liveStream?.playlists ?? null;
  }

  @computed
  get playerSourceLabel(): string | null {
    const { liveCliprHLS, liveStreamUrl } = this.source;

    if (liveCliprHLS) return 'CLIPr';
    if (!liveCliprHLS && liveStreamUrl) return 'My HLS Stream Source';

    return null;
  }

  @computed get scheduledDateTime(): DateTime | null {
    return toValidDateTime(this.liveStream?.scheduledDateTime, { setZone: true });
  }

  // TODO: move these UI-specific labels to a view adapter or table component — the "on" prefix is unnecessary on the entity
  @computed get scheduledOnLabel(): string | null {
    const dateTime = this.scheduledDateTime;
    if (!dateTime)
      return null;

    const date = dateTime.toFormat('DD')
    const time = dateTime.toFormat('t');
    const offset = dateTime.offset / 60;

    return `${date} at ${time} UTC${offset >= 0 ? '+' : ''}${offset < 10 && offset > -10 ? '0' : ''}${offset}:00`;
  }

  @computed get scheduledOrStreamedLabel(): string | null {
    if (this.streamedOnLabel)
      return this.streamedOnLabel;

    if (this.scheduledOnLabel)
      return this.scheduledOnLabel;

    return null;
  }

  @computed
  get liveStreamStartDate(): DateTime | null {
    const dateStr = this.liveStream?.streamStart;
    if (!dateStr)
      return null;
    return DateTime.fromISO(dateStr);
  }

  @computed
  get liveStreamEndDate(): DateTime | null {
    const dateStr = this.liveStream?.streamEnd;
    if (!dateStr)
      return null;
    return DateTime.fromISO(dateStr);
  }

  @computed
  get liveStreamURL(): string | null {
    return this.media?.liveStreamUrl ?? null;
  }

  /** 
   * Gets the actual `streamKey` that will be used for the live stream, depending on whether
   * the primary or the backup feed is selected.
   */
  @computed
  get activeLiveStreamKey(): string | null {
    const { liveStream } = this;
    return (this.source?.liveStreamActivePlaylist === LiveStreamActivePlaylist.Primary ?
      liveStream?.streamKey :
      liveStream?.backupStreamKey) ?? null;
  }

  /** 
   * Gets the actual `ingestUrl` that will be used for the live stream, depending on whether
   * the primary or the backup feed is selected.
   */
  @computed
  get activeLiveIngestUrl(): string | null {
    const { liveStream } = this;
    return (this.source?.liveStreamActivePlaylist === LiveStreamActivePlaylist.Primary ?
      liveStream?.ingestUrl :
      liveStream?.backupIngestUrl) ?? null;
  }

  /** Returns true if the Job is a live job, regardless of it's current status (waiting, streaming or ended). */
  @computed
  get isLive() {
    return this.source?.type === JobSourceType.Live;
  }

  @computed
  get isAudioSource() {
    return !!this.media?.basic;
  }

  @observable updateStatus: 'publishing' | 'idle' = 'idle';
  @observable momentsSyncStatus: SyncStatus = SyncStatus.Empty;
  @observable speakersSyncStatus: SyncStatus = SyncStatus.Empty;
  @observable commentsSyncStatus: SyncStatus = SyncStatus.Empty;
  @observable reactionsSyncStatus: SyncStatus = SyncStatus.Empty

  private momentsFetchPromise: AsyncResult<boolean> | null = null;
  private commentsFetchPromise: AsyncResult<boolean> | null = null;
  private reactionsFetchPromise: AsyncResult<boolean> | null = null;

  @computed
  get isOwnerAuthenticated(): boolean {
    return isOwnerAuthenticatedHelper(this.userId, this.store);
  }

  @computed
  get isPublishing(): boolean {
    return this.updateStatus === 'publishing';
  }

  @computed
  get isLiveStreaming(): boolean {
    if (!this.isLive) // just for safe measure
      return false;

    return this.status === JobStatus.LiveStreaming;
  }

  @computed
  get isLiveStreamWaiting(): boolean {
    if (!this.isLive) // just for safe measure
      return false;

    return this.status === JobStatus.LiveReady || this.status === JobStatus.Pending;
  }

  @computed
  get isLiveStreamEnded(): boolean {
    if (!this.isLive) // just for safe measure
      return false;

    return this.status === JobStatus.LiveEnded;
  }

  // TODO: Investigate why this is needed and replace it with isLiveProcessing if that's the case
  @computed
  get isLiveStreamProcessing(): boolean {
    if (!this.isLive) // just for safe measure
      return false;

    return this.status === JobStatus.InProgress ||
      this.status === JobStatus.Waiting ||
      this.status === JobStatus.InReview ||
      this.status === JobStatus.Done ||
      this.status === JobStatus.Failed ||
      this.status === JobStatus.Updated;
  }

  @computed
  get isLiveProcessing(): boolean {
    return this.liveAggregateStatus === JobLiveAggregateStatus.Processing;
  }

  @computed
  get isLiveFailed(): boolean {
    return this.liveAggregateStatus === JobLiveAggregateStatus.ProcessingFailed;
  }

  @computed
  get isLiveEnded(): boolean {
    return this.liveAggregateStatus === JobLiveAggregateStatus.LiveEnded;
  }

  @computed
  get isLiveProcessingDone(): boolean {
    return this.liveAggregateStatus === JobLiveAggregateStatus.ProcessingDone;
  }

  @computed
  get hasMetadata(): boolean {
    let hasMetadata = false;
    if (!this.metadata)
      return false;

    Object.keys(this.metadata).forEach(key => {
      const metadataKey = key as keyof Metadata;
      const value = this.metadata && this.metadata[metadataKey];
      if (value)
        hasMetadata = true;
    })
    return hasMetadata;
  }

  get posterURL(): string | null {
    const url = (this.poster &&
      this.poster.length > 0 &&
      this.poster[0]?.url) || null;
    if (!url && this.isDocumentJob) {
      return '/document-translation-poster.svg';
    }
    return url;
  }

  get ownerTeamId(): string | null {
    const { ownerId } = this;
    return ownerId?.startsWith(OWNER_TEAM_PREFIX) ?
      ownerId.slice(OWNER_TEAM_PREFIX.length) :
      null;
  }

  get ownerUserId(): string | null {
    const { ownerId } = this;
    return ownerId?.startsWith(OWNER_USER_PREFIX) ?
      ownerId.slice(OWNER_USER_PREFIX.length) :
      null;
  }

  @computed
  get displayOwnerName(): string {
    const { owner } = this;
    if (owner?.team) return owner.team.name;
    return owner.user?.name || owner.user?.username || owner.user?.email!;
  }

  @computed
  get videoDuration(): number {
    return (this.durationInMs / 1000) || 0;
  }

  @computed
  get videoDurationNorm(): number {
    return Math.ceil(this.videoDuration) > Math.round(this.videoDuration) ?
      this.videoDuration :
      Math.round(this.videoDuration);
  }

  @computed
  get isInProgress(): boolean {
    return this.status === 'InProgress';
  }
  @computed
  get isProcessing(): boolean {
    return !!(this.status && ['InReview', 'Waiting', 'Updated'].includes(this.status));
  }
  @computed
  get isFailed(): boolean {
    return this.status === 'Failed';
  }
  @computed
  get isDone(): boolean {
    return this.status === 'Done';
  }
  @computed
  get canRetry(): boolean {
    const retryableStatuses: JobStatus[] = [
      JobStatus.Pending,
      JobStatus.LiveReady,
      JobStatus.LiveEnded,
      JobStatus.Waiting,
      JobStatus.InReview,
      JobStatus.Updated,
      JobStatus.Done,
      JobStatus.Failed,
    ];
    return retryableStatuses.includes(this.status);
  }
  @computed
  get testStatus(): string { // Used for the automated tests
    if (['InReview', 'Waiting', 'Updated'].includes(this.status)) return 'IsProcessing';
    return this.status;
  }

  @computed
  get ellipsedTitle(): string | null {
    if (this.title?.length > 70) {
      return this.title?.substring(0, 69) + '...';
    } else {
      return this.title;
    }
  }

  @computed
  get liveAggregateStatus(): JobLiveAggregateStatus | null {
    const { status } = this;

    // we need to make sure the source is set as 'Live'
    // as most of the status values can overlap the static ones
    // job status alone cannot describe the source of the job (only some specific statuses)
    if (!this.isLive)
      return null;

    // the live status needs to be decoded from the general job status
    switch (status) {
      case JobStatus.Pending:
        return JobLiveAggregateStatus.NotReady;
      case JobStatus.LiveReady: // specific to live
        return JobLiveAggregateStatus.Waiting;

      case JobStatus.LiveStreaming: // specific to live
        return JobLiveAggregateStatus.Streaming;

      case JobStatus.LiveEnded: // specific to live
        return JobLiveAggregateStatus.LiveEnded;

      case JobStatus.Done: // generic
      case JobStatus.InProgress: // generic
      case JobStatus.Updated: // generic
      case JobStatus.InReview: // generic
      case JobStatus.Waiting: // generic
      case JobStatus.Failed: // TODO: verify — Failed may indicate media failure, not live stream failure
        {
          switch (this.mediaAggregatedStatus) {
            case 'IngestCompleted':
              return JobLiveAggregateStatus.ProcessingDone;
            case 'Failed':
              return JobLiveAggregateStatus.ProcessingFailed;
            case 'IngestProcessing':
              return JobLiveAggregateStatus.Processing;
          }
        }
    }

    return null;
  }

  // this marks a combination between job status (overall download and ingest status), live job status and video level ingest status
  // job status = 'Failed' && ingest error = 'DownloadFailed' -> video level status = pending
  @computed
  get isDocumentTranslation(): boolean {
    return this.speciality === JobSpeciality.DocumentTranslation;
  }

  @computed
  get isWarrantFirstDraft(): boolean {
    return this.speciality === JobSpeciality.WarrantFirstDraft;
  }

  /** Document-based jobs reuse the document upload/viewer/routing path (translation + warrant draft). */
  @computed
  get isDocumentJob(): boolean {
    return this.isDocumentTranslation || this.isWarrantFirstDraft;
  }

  @computed
  get mediaAggregatedStatus(): MediaAggregatedStatus | null {
    if (this.isFailed)
      return MediaAggregatedStatus.Failed;

    if (this.isDocumentJob)
      return this.isDone
        ? MediaAggregatedStatus.IngestCompleted
        : MediaAggregatedStatus.IngestProcessing;

    if (this.isLiveStreaming)
      return MediaAggregatedStatus.LiveStreaming;

    if (this.isLiveStreamWaiting)
      return MediaAggregatedStatus.LiveWaiting;

    if (this.isLiveStreamEnded)
      return MediaAggregatedStatus.LiveEnded;

    if (this.isMediaLevelPending
      || this.isMediaLevelProcessing
      || this.isMediaLevelNotRequested)
      return MediaAggregatedStatus.IngestProcessing;

    if (this.isMediaLevelDone)
      return MediaAggregatedStatus.IngestCompleted;

    return null;
  }

  @computed
  get isMediaDone(): boolean {
    if (this.mediaAggregatedStatus === 'IngestCompleted')
      return true;

    return false;
  }

  @computed
  get isMediaAccessible(): boolean {
    if (this.mediaAggregatedStatus &&
      ['LiveStreaming', 'LiveWaiting', 'IngestCompleted'].includes(this.mediaAggregatedStatus))
      return true;

    return false;
  }

  @computed
  get isMediaProcessing(): boolean {
    if (this.mediaAggregatedStatus === 'IngestProcessing')
      return true;

    return false;
  }

  @computed
  get isMediaFailed(): boolean {
    if (this.mediaAggregatedStatus === 'Failed')
      return true;

    return false;
  }

  @observable momentQueryable: MomentQueryable | null = null;

  readonly momentLookup = observable.map<string, MomentModel>();
  momentPageInfo: PageInfo | null = null;

  @computed
  get moments(): MomentModel[] {
    return sortedMoments(this.momentLookup);
  }

  @computed
  get visibleMoments(): MomentModel[] {
    return visibleMomentsFrom(this.moments);
  }

  readonly trackLookup = observable.map<string, TrackModel>();

  @computed
  get tracks(): TrackModel[] {
    return sortedTracks(this.trackLookup)
      .filter(track => !track.isNonSpeech);
  }

  @computed
  get activeTracks(): TrackModel[] {
    return filterActiveTracks(this.tracks);
  }

  @computed
  get transcriptTracks(): TrackModel[] {
    return filterTranscriptTracks(this.tracks);
  }

  @computed
  get momentTracks(): TrackModel[] {
    return filterMomentTracks(this.tracks);
  }

  @computed
  get topicTracks(): TrackModel[] {
    return filterTopicTracks(this.tracks);
  }

  @computed
  get subtopicTracks(): TrackModel[] {
    return filterSubtopicTracks(this.tracks);
  }

  @computed
  get activeTopicTracks(): TrackModel[] {
    return filterActiveTopicTracks(this.topicTracks);
  }

  @computed
  get activeTopicTrack(): TrackModel | null {
    return findActiveTopicTrack(this.topicTracks);
  }

  @computed
  get activeSubtopicTracks(): TrackModel[] {
    return filterActiveSubtopicTracks(this.subtopicTracks);
  }

  @computed
  get activeSubtopicTrack(): TrackModel | null {
    return findActiveSubtopicTrack(this.subtopicTracks);
  }
  // memoize filtered moment lists which are frequently accessed

  @computed
  get transcriptMoments() {
    return filterTranscriptMoments(this.moments);
  }

  @computed
  get topicMoments(): Topic[] {
    return filterTopicMoments(this.moments);
  }

  @computed
  get subtopicMoments(): SubTopic[] {
    return filterSubtopicMoments(this.moments);
  }

  @computed
  get redactionMoments(): Moment[] {
    return filterRedactionMoments(this.moments);
  }

  /** Moments which are not Transcript or Topic or SubTopic. */

  @computed
  get genericMoments() {
    return filterGenericMoments(this.moments);
  }

  @computed
  get answerMoments() {
    return filterAnswerMoments(this.moments);
  }

  @computed
  get questionMoments() {
    return filterQuestionMoments(this.moments);
  }

  /** Returns the 'createdAt' property as an UTC luxon DateTime object. */

  @computed
  get createdAtDate() {
    return DateTime.fromISO(this.createdAt);
  }

  @computed
  get createdAtLabel() {
    const date = this.createdAtDate;
    return (date.toLocaleString(DateTime.DATETIME_FULL));
  }

  @computed
  get createdAtRelativeLabel() {
    const date = this.createdAtDate;

    if (date.diffNow('days').days < -2)
      return date.toFormat('DDD');

    return (
      date.toRelativeCalendar() + ' at ' +
      date.toFormat('HH:mm'));
  }

  @computed
  get relatedBookmarks(): Bookmark[] {
    return relatedBookmarksHelper(this.id, this.store);
  }

  @computed
  get relatedMomentBookmarks(): Bookmark[] {
    return relatedMomentBookmarksHelper(this.relatedBookmarks);
  }

  @computed
  get relatedTopicMomentBookmarks(): Bookmark[] {
    return relatedTopicMomentBookmarksHelper(this.relatedMomentBookmarks);
  }

  @computed
  get relatedGenericMomentBookmarks(): Bookmark[] {
    return relatedGenericMomentBookmarksHelper(this.relatedMomentBookmarks);
  }

  @computed
  get speakerIds(): Set<string> {
    return collectSpeakerIds(this.tracks, this.moments);
  }

  @computed
  get speakers(): SpeakerModel[] {
    return filterSpeakersByIds(this.id, this.speakerIds, this.store);
  }

  @computed
  get privateSpeakers(): SpeakerModel[] {
    return filterPrivateSpeakersByIds(this.id, this.speakerIds, this.store);
  }

  @computed
  get hasPrivateSpeakers(): boolean {
    return this.privateSpeakers.length > 0;
  }

  @computed
  get trackSpeakerIds(): Set<string> {
    return collectTrackSpeakerIds(this.tracks);
  }

  @computed
  get trackSpeakers(): SpeakerModel[] {
    return resolveTrackSpeakers(this.trackSpeakerIds, this.store);
  }

  // Enrichment section
  @computed
  get currentEnrichmentLevel(): EnrichmentItem | null {
    if (!this.features)
      return null;

    EnrichmentItems.forEach((key: EnrichmentItem) => {
      if (this.features && this.features[key] === 'Processing')
        return [key];
    });

    return null;
  }

  @computed
  get enrichmentLevel(): EnrichmentItem | null {
    if (!this.features)
      return null;

    for (let i = EnrichmentItems.length - 1; i >= 0; i--) {
      const key: EnrichmentItem = EnrichmentItems[i];

      if (this.features[key] !== 'NotRequested')
        return key;
    }

    return null;
  }

  @computed
  get mediaLevelStatus(): JobFeatureStatus | null {
    if (this.features)
      return this.features['media'];

    return null;
  }

  @computed
  get isMediaLevelDone(): boolean | null {
    return this.mediaLevelStatus === 'Done';
  }

  @computed
  get isMediaLevelProcessing(): boolean | null {
    return this.mediaLevelStatus === 'Processing';
  }

  @computed
  get isMediaLevelPending(): boolean | null {
    return this.mediaLevelStatus === 'Pending';
  }

  @computed
  get isMediaLevelFailed(): boolean | null {
    return this.mediaLevelStatus === 'Fail';
  }

  @computed
  get isMediaLevelNotRequested(): boolean | null {
    return this.mediaLevelStatus === 'NotRequested';
  }

  @computed
  get automaticEnrichmentLevelStatus(): JobFeatureStatus | null {
    if (this.features)
      return this.features['automaticEnrichment'];

    return null;
  }

  @computed
  get hasAutomaticEnrichmentLevel(): boolean {
    if (this.features)
      return this.features['automaticEnrichment'] !== 'NotRequested';

    return false;
  }

  @computed
  get isAutomaticEnrichmentLevelDone(): boolean {
    if (this.features)
      return this.features['automaticEnrichment'] !== 'Done';

    return false;
  }

  @computed
  get manualEnrichmentLevelStatus(): JobFeatureStatus | null {
    if (this.features)
      return this.features['manualEnrichment'];

    return null;
  }

  @computed
  get hasManualEnrichmentLevel(): boolean {
    if (this.features)
      return this.features['manualEnrichment'] !== 'NotRequested';

    return false;
  }

  @computed
  get hasTranscriptLevel(): boolean {
    if (this.features)
      return this.features['transcript'] !== 'NotRequested';

    return false;
  }

  @computed
  get transcriptLevelStatus(): JobFeatureStatus | null {
    if (this.features)
      return this.features['transcript'];

    return null;
  }

  @computed
  get transcriptStatus(): JobFeatureStatus | null {
    // if (this.hasManualEnrichmentLevel)
    //   return this.manualEnrichmentLevelStatus;

    return this.transcriptLevelStatus;
  }

  @computed
  get isTranscriptProcessing(): boolean {
    return this.transcriptStatus === 'Processing';
  }

  @computed
  get isTranscriptPending(): boolean {
    return this.transcriptStatus === 'Pending';
  }

  @computed
  get isTranscriptDone(): boolean {
    return this.transcriptStatus === 'Done';
  }

  @computed
  get enrichmentStatus(): JobFeatureStatus | null {
    // if (this.hasManualEnrichmentLevel)
    //   return this.manualEnrichmentLevelStatus;

    return this.automaticEnrichmentLevelStatus;
  }

  @computed
  get isEnrichmentProcessing(): boolean {
    return this.enrichmentStatus === 'Processing';
  }

  @computed
  get isEnrichmentPending(): boolean {
    return this.enrichmentStatus === 'Pending';
  }

  @computed
  get isEnrichmentDone(): boolean {
    return this.enrichmentStatus === 'Done';
  }

  @computed
  get isEnrichmentNotRequested(): boolean {
    return this.enrichmentStatus === 'NotRequested';
  }
  // Section end

  readonly commentLookup = observable.map<string, Comment>();
  @computed
  get comments(): Comment[] {
    return sortedComments(this.commentLookup);
  }

  readonly reactionLookup = observable.map<string, Reaction>();
  @computed
  get reactions(): Reaction[] {
    return sortedReactions(this.reactionLookup);
  }

  @observable addedReaction: Nullable<Reaction> = null;

  @computed
  private get api() {
    return this.store.api;
  }

  async fetchDependencies(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {

    const [
      [, err1],
      [, err2]
    ] = await Promise.all([
      this.apiFetchTracks(opts),
      this.apiFetchMoments(false, opts)
    ]);

    // need to have the tracks and moments loaded in order to determine the list of used speakers
    const [, err3] = await this.apiBatchFetchSpeakers(opts);

    if (err1 || err2 || err3)
      return [null, err1 || err2 || err3];
    return [true];
  }

  async fetchMoments(opts?: ApiQueryOptions): AsyncResult<boolean> {
    const fetch = async (opts?: ApiQueryOptions): AsyncResult<boolean> => {
      this.setMomentsSyncStatus(SyncStatus.Fetching);

      const [
        [, err1],
        [, err2]
      ] = await Promise.all([
        this.apiFetchTracks(opts),
        this.apiFetchMoments(false, opts)
      ]);

      if (err1 || err2) {
        this.setMomentsSyncStatus(SyncStatus.Error);
        return [null, err1 || err2];
      }

      this.setMomentsSyncStatus(SyncStatus.Fetched);
      return [true, null];
    }

    if (this.momentsSyncStatus === SyncStatus.Fetching && this.momentsFetchPromise) {
      return this.momentsFetchPromise;
    }

    this.momentsFetchPromise = fetch(opts);
    return this.momentsFetchPromise;
  }

  @action
  setMomentsSyncStatus(status: SyncStatus) {
    this.momentsSyncStatus = status;
  }

  resetSyncStatuses() {
    this.momentsSyncStatus = SyncStatus.Empty;
    this.commentsSyncStatus = SyncStatus.Empty;
    this.reactionsSyncStatus = SyncStatus.Empty;
    this.speakersSyncStatus = SyncStatus.Empty;
  }

  async handlePublishVideo() {
    if (this.isPublishing) return;

    this.publicToggleModel.value = `${!this.isPublic}`;
    this.updateStatus = 'publishing';
    const args: UpdateJobInput = {
      id: this.id,
      isPublic: !this.isPublic,
    };
    const [, err] = await this.store.jobManager.apiUpdateJob(args, true);
    if (err) {
      notifyError(this, 'Your video status was not updated.');
    } else {
      notifySuccess(this, `Your video is now ${this.isPublic ? 'private.' : 'shareable.'}`);

      if (!this.isPublic) {
        this.store.analyticsService.registerEvent(
          AnalyticsEventTypes.VideoMakePublicType,
          {
            jobId: this.id,
            job: this
          }
        )
      }
    }
    this.updateStatus = 'idle';
  }

  // #region Speakers
  // -------
  async apiFetchSpeakers(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiFetchSpeakersHelper(this, opts);
  }

  async apiBatchFetchSpeakers(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiBatchFetchSpeakersHelper(this, opts);
  }

  @action
  private setSpeakersSyncStatus(status: SyncStatus) {
    this.speakersSyncStatus = status;
  }

  async addSpeaker(props: ApiVariables<'addSpeaker'>)
    : AsyncResult<boolean> {
    return addSpeakerHelper(this.store, props);
  }

  async apiUpdateSpeaker(props: ApiVariables<'updateSpeaker'>)
    : AsyncResult<boolean> {
    return apiUpdateSpeakerHelper(this.store, props);
  }
  // #endregion

  // #region Tracks
  // -------
  hasTrack(id: string): boolean {
    return hasTrackInLookup(this.trackLookup, id);
  }

  getTrack(id: string): TrackModel | null {
    return getTrackFromLookup(this.trackLookup, id);
  }

  maybeGetTrackByName(name: string): TrackModel | null {
    return findTrackByName(this.tracks, name);
  }

  maybeGetTranscriptTrackBySpeaker(speakerId: string | null): TrackModel | null {
    return findTranscriptTrackBySpeaker(this.transcriptTracks, speakerId);
  }

  maybeGetMomentTrackByMomentType(momentType: string | null): TrackModel | null {
    return findMomentTrackByMomentType(this.momentTracks, momentType);
  }

  @action
  insertTrack(props: TrackProps) {
    return insertTrackIntoLookup(props, this.trackLookup, this.store);
  }

  async apiFetchTracks(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiFetchTracksHelper(this, opts);
  }

  async apiAddTrack(args: ApiVariables<'addTrack'>)
    : AsyncResult<TrackModel> {
    return apiAddTrackHelper(this, args);
  }

  async apiUpdateTrack(args: ApiVariables<'updateTrack'>, deps: ('tracks' | 'moments')[] = ['tracks'])
    : AsyncResult<TrackModel> {
    return apiUpdateTrackHelper(this, args, deps);
  }

  async apiMergeTracks(args: ApiVariables<'mergeTracks'>)
    : AsyncResult<Boolean> {
    return apiMergeTracksHelper(this, args);
  }

  async apiDuplicateTrack(args: ApiVariables<'duplicateTrack'>)
    : AsyncResult<TrackModel> {
    return apiDuplicateTrackHelper(this, args);
  }
  // #endregion

  // #region Moments
  // -------
  hasMoment(id: string): boolean {
    return hasMomentInLookup(this.momentLookup, id);
  }

  getMoment(id: string): MomentModel | null {
    return getMomentFromLookup(this.momentLookup, id);
  }

  // it refers to the active topic track topic moments
  @computed
  get activeTopics(): Topic[] {
    return filterActiveTopics(this.topicMoments, this.activeTopicTrack);
  }

  @computed
  get activeSubtopics(): SubTopic[] {
    return filterActiveSubtopics(this.subtopicMoments, this.activeSubtopicTrack);
  }

   // it refers to the active topic track interview moments
   @computed
   get activeAnswers(): Answer[] {
     return filterActiveAnswers(this.answerMoments, this.activeTopicTrack);
   }

   @computed
   get activeQuestions(): Question[] {
     return filterActiveQuestions(this.questionMoments, this.activeSubtopicTrack);
   }

   getQuestionsOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.questionMoments.filter(moment =>
      (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
    );
  }

  getActiveQuestionsOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.activeQuestions.filter(moment =>
      (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
    );
  }

  getActiveQuestionsContainingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.activeQuestions.filter(moment =>
      (moment.startTimeNorm <= start && moment.endTimeNorm >= end)
    );
  }
  
  getInterviewMomentParent(moment: MomentModel): MomentModel | null { // parent is determined by checking the first overlapping active moment(question)
    const overlappingQuestions = this.getActiveQuestionsOverlappingInterval(moment.startTimeNorm, moment.endTimeNorm);
    return overlappingQuestions.find(el => el.id !== moment.id) || null;
  }
  
  getInterviewIntervalParent(start: number, end: number, excludeIds?: string[]): MomentModel | null { // parent is determined by checking the first overlapping active moment(question) that is not excluded
    return this.getActiveQuestionsOverlappingInterval(start, end)
      .find(el => (excludeIds ? !excludeIds.includes(el.id) : el)) || null;
  }
  
  isInterviewIntervalContained(start: number, end: number, excludeIds?: string[]): boolean { // check if it's contained in parent boundaries
    start = timestampNorm(start);
    end = timestampNorm(end);
    const parent = this.getInterviewIntervalParent(start, end, excludeIds);
    return !!(parent && (start >= parent.startTimeNorm) && (end <= parent.endTimeNorm));
  }
  
  getAnswersOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining children
    start = timestampNorm(start);
    end = timestampNorm(end);
    return [
      ...this.answerMoments?.filter(moment =>
        (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
      )
    ];
  }
  
  getActiveAnswersOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining children
    start = timestampNorm(start);
    end = timestampNorm(end);
    return [
      ...this.activeAnswers.filter(moment =>
        (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
      )
    ];
  }
  
  getInterviewMomentChildren(moment: MomentModel): MomentModel[] {
    return this.getActiveAnswersOverlappingInterval(moment.startTimeNorm, moment.endTimeNorm);
  }
  
  getInterviewIntervalChildren(start: number, end: number): MomentModel[] {
    return this.getActiveAnswersOverlappingInterval(start, end);
  }
  
  @computed
  get uncontainedAnswers(): MomentModel[] {
    return [
      ...this.answerMoments.filter(moment =>
        !this.questionMoments.some(question =>
          (question.startTimeNorm <= moment.startTimeNorm && question.endTimeNorm >= moment.endTimeNorm) &&
          (question.endTimeNorm > moment.startTimeNorm) // to exclude adimensional weird cases
        ))
    ]
  }
  
  @computed
  get uncontainedActiveAnswers(): MomentModel[] {
    return [
      ...this.activeAnswers.filter(moment =>
        !this.activeQuestions.some(question =>
          (question.startTimeNorm <= moment.startTimeNorm && question.endTimeNorm >= moment.endTimeNorm) &&
          (question.endTimeNorm > moment.startTimeNorm) // to exclude adimensional weird cases
        ))
    ]
  }

  // everything is normalized to match the player seeker/selector
  getTopicsOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.topicMoments.filter(moment =>
      (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
    );
  }

  // everything is normalized to match the player seeker/selector
  getActiveTopicsOverlappingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.activeTopics.filter(moment =>
      (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
    );
  }

  getActiveTopicsContainingInterval(start: number, end: number): MomentModel[] { // base for determining the parent
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.activeTopics.filter(moment =>
      (moment.startTimeNorm <= start && moment.endTimeNorm >= end)
    );
  }

  getMomentParent(moment: MomentModel): MomentModel | null { // parent is determined by checking the first overlapping active moment(topic)
    const overlappingTopics = this.getActiveTopicsOverlappingInterval(moment.startTimeNorm, moment.endTimeNorm);
    return overlappingTopics.find(el => el.id !== moment.id) || null;
  }

  getIntervalParent(start: number, end: number, excludeIds?: string[]): MomentModel | null { // parent is determined by checking the first overlapping active moment(topic) that is not excluded
    return this.getActiveTopicsOverlappingInterval(start, end)
      .find(el => excludeIds ? !excludeIds.includes(el.id) : el) || null;
  }

  isIntervalContained(start: number, end: number, excludeIds?: string[]): boolean { // check if it's contained in parent boundaries
    start = timestampNorm(start);
    end = timestampNorm(end);
    const parent = this.getIntervalParent(start, end, excludeIds);
    return !!(parent && (start >= parent.startTimeNorm) && (end <= parent.endTimeNorm));
  }

  getSubtopicsOverlappingInterval(start: number, end: number): MomentModel[] { // base for detemining children
    start = timestampNorm(start);
    end = timestampNorm(end);
    return [
      ...this.subtopicMoments?.filter(moment =>
        (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
      )
    ];
  }

  getActiveSubtopicsOverlappingInterval(start: number, end: number): MomentModel[] { // base for detemining children
    start = timestampNorm(start);
    end = timestampNorm(end);
    return [
      ...this.activeSubtopics.filter(moment =>
        (moment.startTimeNorm < end && moment.endTimeNorm > start) || moment.startTimeNorm === start
      )
    ];
  }

  getMomentChildren(moment: MomentModel): MomentModel[] {
    return this.getActiveSubtopicsOverlappingInterval(moment.startTimeNorm, moment.endTimeNorm);
  }

  getIntervalChildren(start: number, end: number): MomentModel[] {
    return this.getActiveSubtopicsOverlappingInterval(start, end);
  }

  getIntervalUncontainedChildren(start: number, end: number): MomentModel[] {
    start = timestampNorm(start);
    end = timestampNorm(end);
    return this.getIntervalChildren(start, end)
      .filter(el =>
        el.startTimeNorm < start ||
        el.endTimeNorm > end
      );
  }
  @computed
  get uncontainedSubtopics(): MomentModel[] {
    return [
      ...this.subtopicMoments.filter(moment =>
        !this.topicMoments.some(topic =>
          (topic.startTimeNorm <= moment.startTimeNorm && topic.endTimeNorm >= moment.endTimeNorm) &&
          (topic.endTimeNorm > moment.startTimeNorm) // to exclude adimensional weird cases
        ))
    ]
  }

  @computed
  get uncontainedActiveSubtopics(): MomentModel[] {
    return [
      ...this.activeSubtopics.filter(moment =>
        !this.activeTopics.some(topic =>
          (topic.startTimeNorm <= moment.startTimeNorm && topic.endTimeNorm >= moment.endTimeNorm) &&
          (topic.endTimeNorm > moment.startTimeNorm) // to exclude adimensional weird cases
        ))
    ]
  }

  @computed
  get multipleActiveTrackTypes(): string[] {
    return findMultipleActiveTrackTypes(this.activeTracks);
  }

  validateSetToDone(): ValidatorResponse {
    return validateSetToDoneHelper(this.multipleActiveTrackTypes, this.uncontainedActiveSubtopics);
  }

  getActiveDuplicateByTrack(track: Pick<TrackModel, 'type' | 'momentType' | 'speakerId'> & { id?: string }) {
    return findActiveDuplicateByTrack(this.activeTracks, track);
  }

  checkIfActiveDuplicateForTrack(track: Pick<TrackModel, 'type' | 'momentType' | 'speakerId'> & { id?: string }) {
    return checkIfActiveDuplicateForTrackHelper(this.activeTracks, track);
  }

  maybeGetMoment(id?: Maybe<string>): MomentModel | null {
    return maybeGetMomentFromLookup(this.momentLookup, id);
  }

  @action
  insertMoment(props: Partial<MomentProps>) {
    return insertMomentIntoLookup(props, this.momentLookup, this.store);
  }

  @action
  private getFetchMomentsArgs(more: boolean = false) {
    return buildFetchMomentsArgs(this.id, more, this.momentPageInfo);
  }

  async apiFetchMoments(more: boolean = false, opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiFetchMomentsHelper(this, more, opts);
  }

  async apiFetchMoment(momentId: string, opts?: ApiQueryOptions)
    : AsyncResult<GetMomentQuery> {
    return apiFetchMomentHelper(this, momentId, opts);
  }

  async apiAddMoment(input: AddMomentInput, updateJob = true)
    : AsyncResult<string> {
    return apiAddMomentHelper(this, input, updateJob);
  }

  async apiUpdateMoment(input: UpdateMomentInput)
    : AsyncResult<string> {
    return apiUpdateMomentHelper(this, input);
  }

  async apiUpdateMoments(input: UpdateMomentInput[])
    : AsyncResult<boolean> {
    return apiUpdateMomentsHelper(this, input);
  }

  async apiDeleteMoment(input: DeleteMomentInput)
    : AsyncResult<string> {
    return apiDeleteMomentHelper(this, input);
  }

  async apiDeleteMoments(input: DeleteMomentInput[])
    : AsyncResult<boolean> {
    return apiDeleteMomentsHelper(this, input);
  }
  // #endregion

  // #region Comments and Reactions API methods

  async apiFetchComments(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiFetchCommentsHelper(this, opts);
  }

  async fetchComments(opts?: ApiQueryOptions): AsyncResult<boolean> {
    return fetchCommentsHelper(this, this.commentsFetchPromise, (p) => { this.commentsFetchPromise = p; }, opts);
  }

  @action
  setCommentsSyncStatus(status: SyncStatus) {
    this.commentsSyncStatus = status;
  }

  async apiFetchReactions(opts?: ApiQueryOptions)
    : AsyncResult<boolean> {
    return apiFetchReactionsHelper(this, opts);
  }

  async fetchReactions(opts?: ApiQueryOptions): AsyncResult<boolean> {
    return fetchReactionsHelper(this, this.reactionsFetchPromise, (p) => { this.reactionsFetchPromise = p; }, opts);
  }

  @action
  setReactionsSyncStatus(status: SyncStatus) {
    this.reactionsSyncStatus = status;
  }

  async apiAddComment(input: AddCommentInput, opts?: ApiMutationOptions)
    : AsyncResult<Comment> {
    return apiAddCommentHelper(this, input, opts);
  }

  async apiAddReply(input: AddReplyInput, opts?: ApiMutationOptions)
    : AsyncResult<Comment> {
    return apiAddReplyHelper(this, input, opts);
  }

  async apiEditComment(input: EditCommentInput, opts?: ApiMutationOptions)
    : AsyncResult<Comment> {
    return apiEditCommentHelper(this, input, opts);
  }

  async apiDeleteComment(vars: ApiVariables<'deleteComment'>, opts?: ApiMutationOptions)
    : AsyncResult<Comment | null> {
    return apiDeleteCommentHelper(this, vars, opts);
  }

  async apiAddReaction(input: AddReactionInput, opts?: ApiMutationOptions)
    : AsyncResult<Reaction> {
    return apiAddReactionHelper(this, input, opts);
  }

  @action
  setAddedReaction = (rct: Reaction | null) => {
    this.addedReaction = rct;
  }
  // #endregion

  // api misc
  async apiGetJobTranscript(input: Omit<ApiVariables<'getJobTranscript'>, 'jobId'>, opts?: ApiQueryOptions)
    : AsyncResult<string | null> {

    const args = Object.assign(input, {
      jobId: this.id
    });

    const [res, err] = await this.api.getJobTranscript(args, opts);

    if (err)
      return [null, err];

    const url = res?.getJobTranscript?.url ?? null;

    return [url];
  }
  // #endregion

  // #region Comments and Reaction data
  @action
  insertComment(props: Partial<CommentProps>) {
    return insertCommentIntoLookup(props, this.commentLookup, this.store);
  }
  /**
   * Removes a Comment from the store.
   * This is only a local method. If you want to make an API delete mutation use `apiDeleteComment`.
   */
  @action
  deleteComment(id: string): Comment | null {
    return deleteCommentFromLookup(id, this.commentLookup);
  }

  /** Returns the `Comment` with the requested `id` if it has been loaded into the `Job`, or `null` otherwise. */
  getComment(id?: Maybe<string>): Comment | null {
    return getCommentFromLookup(this.commentLookup, id);
  }

  @action
  insertReaction(props: Partial<ReactionProps>): Reaction {
    return insertReactionIntoLookup(props, this.reactionLookup, this.store);
  }

  /**
   * Removes a `Reaction` from the store.
   * This is only a local method. If you want to make an API delete mutation use `apiDeleteReaction`.
   */
  @action
  deleteReaction(id: string): Reaction | null {
    return deleteReactionFromLookup(id, this.reactionLookup);
  }

  /** Returns the `Reaction` with the requested `id` if it has been loaded into the `Job`, or `null` otherwise. */
  getReaction(id?: Maybe<string>): Reaction | null {
    return getReactionFromLookup(this.reactionLookup, id);
  }
  // #endregion

  toString() {
    return '{ Job }';
  }

  async copyStreamKey() {
    if (this.liveStream?.streamKey) {
      await navigator.clipboard?.writeText(this.liveStream?.streamKey);
      notifySuccess(this, 'Server key copied to clipboard!');
    }
  }

  async copyIngestUrl() {
    if (this.liveStream?.ingestUrl) {
      await navigator.clipboard?.writeText(this.liveStream?.ingestUrl);
      notifySuccess(this, 'Server URL copied to clipboard!');
    }
  }

  async copyBackupStreamKey() {
    if (this.liveStream?.backupStreamKey) {
      await navigator.clipboard?.writeText(this.liveStream?.backupStreamKey);
      notifySuccess(this, 'Server key copied to clipboard!');
    }
  }

  async copyBackupIngestUrl() {
    if (this.liveStream?.backupIngestUrl) {
      await navigator.clipboard?.writeText(this.liveStream?.backupIngestUrl);
      notifySuccess(this, 'Server URL copied to clipboard!');
    }
  }

  durationToString(format: 'card' = 'card'): string {
    return durationToString(this.videoDuration, format);
  }

  durationToTimeCode(format: string): string | null {
    switch (format) {
      case 'card':
        if (!this.videoDuration)
          return null;

        const dur = Duration.fromObject({ seconds: this.videoDuration });
        const hmsDur = dur.shiftTo('hours', 'minutes', 'seconds');

        const mins = Math.round(hmsDur.minutes).toString().padStart(2, '0');
        const secs = Math.round(hmsDur.seconds).toString().padStart(2, '0');
        const hrs = Math.round(hmsDur.hours).toString().padStart(2, '0');

        return `${hrs !== '00' ? hrs + ':' : ''}${mins}:${secs}`;
    }

    return null;
  }

  hasPermission(type: PermissionType) {
    return checkPermission(this, type);
  }

  getPlaylistSource(playlist: LiveStreamActivePlaylist) {
    switch (playlist) {
      case LiveStreamActivePlaylist.External:
        return this.playlists?.external;

      case LiveStreamActivePlaylist.Primary:
        return this.playlists?.primary;

      case LiveStreamActivePlaylist.Secondary:
        return this.playlists?.secondary;
    }
  }

  isPlaylistActive(playlist: LiveStreamActivePlaylist): boolean {
    switch (playlist) {
      case LiveStreamActivePlaylist.External:
        return (!!this.playlists?.external && this.isLiveStreaming) ?? false;

      case LiveStreamActivePlaylist.Primary:
        return this.playlists?.primaryActive ?? false;

      case LiveStreamActivePlaylist.Secondary:
        return this.playlists?.secondaryActive ?? false;
    }
  }

  isPlaylistSelected(playlist: LiveStreamActivePlaylist): boolean {
    return this.source?.liveStreamActivePlaylist === playlist;
  }

  recordingAreAvailable(channel: LiveStreamChannels) {
    switch (channel) {
      case 'Primary': {
        return this.recordings?.primary;
      }

      case 'Secondary': {
        return this.recordings?.secondary;
      }
    }
  }

  isAiProcessing(library: GenerativeAiProvider): boolean {
    return this.isDone && this.hasAutomaticEnrichmentLevel && !this.availableGptProviders?.includes(library);
  }

  isAiDone(library: GenerativeAiProvider): boolean {
    return this.isDone && this.hasAutomaticEnrichmentLevel && !!this.availableGptProviders?.includes(library);
  }
}
